// src/ai/ChatAICliente.js

import React, { useState, useMemo, useRef, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { caricaMenuLocale, caricaPreferenzeAI } from "../api/aiService";
import "./chatAICliente.css";

// ==========================
// COMPONENTE CHAT AI CLIENTE
// ==========================
function ChatAICliente({ localeTag, menu = [], open, onClose }) {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
  {
    role: "assistant",
    content:
      "Ciao! 👋 Sono Qira. Posso aiutarti a scegliere dal menu, consigliarti piatti, abbinamenti o darti una mano con allergeni e preferenze 😊",
  },
]);
    }
  }, [open]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const menuCacheRef = useRef(null);
  const prefCacheRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  // Carica menu + preferenze una sola volta all'apertura
  useEffect(() => {
    if (!open || !localeTag) return;
    if (menuCacheRef.current) return;
    const carica = async () => {
      try {
        const [m, p] = await Promise.all([
          caricaMenuLocale(localeTag),
          caricaPreferenzeAI(localeTag)
        ]);
        menuCacheRef.current = m;
        prefCacheRef.current = p;
      } catch (e) {
        console.warn("ChatAI: errore caricamento cache", e);
      }
    };
    carica();
  }, [open, localeTag]);

  const piattiDisponibili = useMemo(() => {
    if (!Array.isArray(menu)) return [];
    return menu;
  }, [menu]);

  // ==========================
  // INVIO MESSAGGIO
  // ==========================
  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    const historySnapshot = [...messages];

    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setInput("");
    setIsLoading(true);

    try {
      const menuLocale = menuCacheRef.current || menu;
      const preferenze = prefCacheRef.current || {
        obiettivo: "equilibrato",
        tono: "normale",
        intensita: "moderata",
        lingua: "italiano"
      };

      const functions = getFunctions();
      const fn = httpsCallable(functions, "assistenteAI");

      const res = await fn({
        localeTag,
        menu: menuLocale,
        preferenze,
        scenario: null,
        messaggio: userMessage,
        history: historySnapshot.map(m => ({ role: m.role, content: m.content }))
      });

      const risposta = res?.data?.risposta || "Mi spiace, non ho trovato una risposta al momento.";
      setMessages(prev => [...prev, { role: "assistant", content: risposta }]);

    } catch (err) {
      console.error("ERRORE CHAT AI:", err);
      setMessages(prev => [...prev, { role: "assistant", content: "Mi spiace, si è verificato un errore. Riprova." }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="chat-ai-overlay">
      <div className="chat-ai-panel">

        {/* HEADER */}
        <div className="chat-ai-header">
          <div className="chat-ai-title">
            <div className="chat-ai-avatar">
  Q
  <span className="chat-ai-status"></span>
</div>
<div className="chat-ai-text">
  <div className="chat-ai-name">Qira</div>
  <div className="chat-ai-sub">Assistente del menu • Ti aiuto a scegliere</div>
</div>
          </div>
          <button className="chat-ai-close" onClick={onClose} aria-label="Chiudi chat">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* MESSAGGI */}
        <div className="chat-ai-messages">

          {messages.map((msg, i) => (
            <div key={i} className={`chat-ai-message ${msg.role === "user" ? "user" : "assistant"}`}>
              {msg.content.split("\n").map((line, li, arr) => (
                <span key={li}>{line}{li < arr.length - 1 && <br/>}</span>
              ))}
            </div>
          ))}

          {isLoading && (
            <div className="chat-ai-typing">
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-text">Qira sta scrivendo...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* INPUT */}
        <div className="chat-ai-input-area">
          <input
            type="text"
            value={input}
            placeholder="Chiedi qualcosa sul menu..."
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          />
          <button onClick={sendMessage} disabled={isLoading || !input.trim()}>
            {isLoading ? "..." : "Invia"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatAICliente;
